import ArrowRightIcon from '@/common/assets/icons/arrowRightIcon.svg?react';
import { MapInputType } from '@/common/components/base/input/mapInput/MapInput.type';
import MapModal from '@/common/components/base/input/mapInput/modals/map/MapModal';
import { MapAddressFetchedHandler } from '@/common/components/base/input/mapInput/modals/map/MapModal.Type';
import MapEntranceModal from '@/common/components/base/input/mapInput/modals/mapEntrance/MapEntranceModal';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { TextInputType } from '@/common/components/base/input/textInput/TextInput.type';
import { CurrentPositionType } from '@/common/components/base/map/map.type';
import Typography from '@/common/components/base/typography/Typography';
import useModal from '@/common/hooks/useModal';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box } from 'styled-system/jsx';
import { token } from 'styled-system/tokens';

const TextInputWithSelectOnMap = ({
  name,
  label,
  entranceModalText,
  mapModalTitle,
  rules,
  phiName = `${name}_phi`,
  lambdaName = `${name}_lambda`,
}: TextInputType & {
  entranceModalText: MapInputType['entranceModalText'];
  mapModalTitle: MapInputType['mapModalTitle'];
  phiName?: string;
  lambdaName?: string;
}) => {
  const [location, setLocation] = useState<CurrentPositionType | null>(null);

  const { setValue, control, watch, trigger, getValues } = useFormContext();
  const { address } = useWatch({ control });
  const selectedLabel = watch(name);

  // seed the map with the already-saved coordinates (if any) so the map
  // opens on the saved address instead of an empty state
  const savedPhi = getValues(phiName);
  const savedLambda = getValues(lambdaName);
  const hasSavedCoordinates =
    typeof savedPhi === 'number' &&
    typeof savedLambda === 'number' &&
    !Number.isNaN(savedPhi) &&
    !Number.isNaN(savedLambda);
  const savedLocation: CurrentPositionType | null = hasSavedCoordinates
    ? { lat: savedPhi, lng: savedLambda }
    : null;

  const {
    open: openEntrance,
    openModal: openEntranceModal,
    closeModal: closeEntranceModal,
  } = useModal();
  const {
    open: openMap,
    openModal: openMapModal,
    closeModal: closeMapModal,
  } = useModal();

  const onSubmit = () => {
    setValue(name, address.label);
    trigger(name);
    closeMapModal();
  };

  const handleAddressFetched: MapAddressFetchedHandler = (
    response,
    coordinates,
  ) => {
    setValue(name, response.address ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (coordinates?.lat != null && coordinates?.lng != null) {
      setValue(phiName, coordinates.lat, { shouldDirty: true });
      setValue(lambdaName, coordinates.lng, { shouldDirty: true });
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={openEntranceModal}
      >
        <Typography textStyle="b2">{label}</Typography>
        <Typography
          textStyle="b4"
          color="purple-2"
          display="flex"
          alignItems="center"
        >
          انتخاب از روی نقشه
          <ArrowRightIcon width="11px" stroke={token('colors.purple-2')} />
        </Typography>
      </Box>
      <TextInput
        hasLabel={false}
        name={name}
        placeholder={label}
        rules={rules}
      />
      <MapEntranceModal
        open={openEntrance}
        closeModal={closeEntranceModal}
        openMapModal={openMapModal}
        EntranceText={entranceModalText}
      />
      <MapModal
        open={openMap}
        closeModal={closeMapModal}
        title={mapModalTitle}
        onSubmit={onSubmit}
        location={location ?? savedLocation}
        setLocation={setLocation}
        onAddressFetched={handleAddressFetched}
        initialAddress={selectedLabel}
      />
    </Box>
  );
};

export default TextInputWithSelectOnMap;
